import { remoteIssueUrl, type ExtensionId, type UnroutedTask } from '@forecastify/shared';
import { Badge, Button, Icon, ListPager, ListPagerTotal, Tooltip } from '@forecastify/ui';
import type { Translate } from '../../i18n';
import type { Pages } from '../use-pages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ J-3 · the synced tasks no line item claims, beside the parked hours.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner, 15 Oct 2026: *"these tasks should be saved as well, and show up similarly to
 * how unmappable worklogs are, so admins can see them and make the necessary configuration
 * changes."* They were saved already — `MISC-n`, no project — and listed nowhere but an alert.
 *
 * ⚠️ **Grouped by remote project, as the server orders them**, because a rule is written per remote
 * project: somebody fixing these reads `HSO`'s twelve, adds the condition, and watches them leave.
 *
 * ⚠️ **Two ways out, and the tab names both**: a rule on a line item (Szerződések), which files every
 * task it matches now and every issue that arrives later; or a line item picked on the task itself,
 * for the one-off. There is no discard — the task exists and may already hold hours.
 *
 * ⚠️ **A real pager**, unlike the parked tab's single read of 200: an unrouted Jira project
 * contributes every one of its issues, and Loginet's first rehearsal held hundreds.
 */
export function UnroutedTab({
  t,
  rows,
  pages,
  instance,
  onOpenTask,
  onContracts,
}: {
  readonly t: Translate;
  /** ⚠️ Null until it lands — *"nothing waiting"* and *"not read yet"* are different facts. */
  readonly rows: readonly UnroutedTask[] | null;
  readonly pages: Pages;
  /** What a link to the tracker's own issue is built from. */
  readonly instance: { readonly baseUrl: string; readonly extensionId: ExtensionId };
  readonly onOpenTask: (key: string) => void;
  readonly onContracts: () => void;
}) {
  const waiting = rows ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="integration-unrouted">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[13px] font-medium">{t('integrations.unrouted.heading')}</h2>
          <p className="text-muted-foreground text-[11.5px]">
            {rows === null ? null : (
              <ListPagerTotal
                total={pages.total}
                labels={{
                  pageTotal: (total) =>
                    total === 0
                      ? t('integrations.unrouted.none')
                      : t('integrations.unrouted.waiting', { count: total }),
                }}
              />
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onContracts}>
          <Icon name="file-text" size={13} />
          {t('integrations.unrouted.toContracts')}
        </Button>
      </div>

      {rows === null ? null : waiting.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border px-3 py-6 text-center text-[12px]">
          {t('integrations.unrouted.emptyBody')}
        </div>
      ) : (
        <>
          <p className="text-muted-foreground text-[12px] leading-relaxed">
            {t('integrations.unrouted.explain')}
          </p>
          <div className="overflow-hidden rounded-lg border">
            <div className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[11px] font-medium tracking-wide uppercase">
              <span className="w-[120px] shrink-0">{t('integrations.unrouted.col.project')}</span>
              <span className="w-[92px] shrink-0">{t('integrations.unrouted.col.issue')}</span>
              <span className="min-w-0 flex-1">{t('integrations.unrouted.col.title')}</span>
              <span className="hidden w-[160px] shrink-0 lg:block">
                {t('integrations.unrouted.col.components')}
              </span>
              <span className="hidden w-[110px] shrink-0 md:block">
                {t('integrations.unrouted.col.status')}
              </span>
            </div>
            {waiting.map((row) => {
              const url = remoteIssueUrl({ ...instance, remoteKey: row.remoteKey });
              return (
                <div
                  key={row.taskId}
                  className="flex items-center gap-3 border-b px-3 py-2 text-[12.5px] last:border-0"
                >
                  <Tooltip asChild content={row.remoteProjectName}>
                    <span className="w-[120px] shrink-0 truncate text-[11.5px]">
                      {row.remoteProjectKey ?? row.remoteProjectId ?? '–'}
                    </span>
                  </Tooltip>
                  <span className="w-[92px] shrink-0 truncate text-[11.5px]">
                    {url === null ? (
                      (row.remoteKey ?? '–')
                    ) : (
                      <a href={url} target="_blank" rel="noreferrer">
                        {row.remoteKey}
                      </a>
                    )}
                  </span>
                  {/* ⚠️ The title opens OUR task, which is where a line item is picked by hand;
                      its own key rides along muted, because `MISC-488` is what a log names. */}
                  <button
                    type="button"
                    className="hover:text-primary min-w-0 flex-1 truncate text-left"
                    onClick={() => {
                      onOpenTask(row.key);
                    }}
                  >
                    {row.title}
                    <span className="text-muted-foreground ml-2 text-[11px]">{row.key}</span>
                  </button>
                  <span className="hidden w-[160px] shrink-0 truncate text-[11.5px] lg:block">
                    {row.components.length === 0 ? '–' : row.components.join(', ')}
                  </span>
                  <span className="hidden w-[110px] shrink-0 md:block">
                    {row.remoteStatus === null ? null : (
                      <Badge variant="outline">{row.remoteStatus}</Badge>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end">
            <ListPager
              page={{
                hasPrevious: pages.hasPrevious,
                hasNext: pages.hasNext,
                onPrevious: pages.previous,
                onNext: pages.next,
                index: pages.index,
                count: pages.count,
                total: pages.total,
                onGoTo: pages.goTo,
              }}
              labels={{
                previousPage: t('list.previousPage'),
                nextPage: t('list.nextPage'),
                pageSelector: t('list.pageSelector'),
                pageOf: (page, count) => t('list.pageOf', { page, count }),
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
